import { useEssentials } from 'hooks/utils/useEssentials'
import { useNddHierarchyMetrics } from 'hooks/ndd/useNddMetrics'
import { Grid } from '@design-systems/canvas-ui'
import { formattedTimeStamp } from 'pages/fulfillment/helpers'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import useCountdown from 'hooks/utils/useCountdown'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { buildNddRgdPayload } from './ndd-rgd-helpers'
import { useMemo } from 'react'

export const StoreFulfillmentNddRGD = () => {
    const { t, env, session, currentLocation, logout } = useEssentials()
    const { storeFulfillment } = TRANSLATION_KEYS

    const {
        data: nddData,
        isLoading,
        isError,
        error,
        refetch,
    } = useNddHierarchyMetrics({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const count = useCountdown(5, logout, error as Error)

    const nddTitle = useMemo(() => {
        // Use current time since NDD data doesn't include a specific timestamp
        const timestamp = new Date().toISOString()
        return `${t(storeFulfillment.titles.todayAsOf)} ${formattedTimeStamp(timestamp)}`
    }, [t, storeFulfillment.titles.todayAsOf])

    const payload = useMemo(() => {
        if (!nddData) return {}

        const headings = {
            location: storeFulfillment.headings.location,
            containersToSort: storeFulfillment.headings.containersToSort,
            infPercent: storeFulfillment.headings.percentINF,
            sortedOnTime: storeFulfillment.headings.sortedOnTime,
            handoffAccuracy: storeFulfillment.headings.handoffAccuracy,
        }

        const toggles = {
            showStores: storeFulfillment.toggles.showStores,
            showDistricts: storeFulfillment.toggles.showDistricts,
            viewInPercent: 'filters.viewInPercent',
        }

        return buildNddRgdPayload(nddData, {
            headings,
            currentLocation,
            t,
            toggles,
        })
    }, [nddData, currentLocation, t, storeFulfillment])

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }

    if (isError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    if (!nddData) {
        return <ErrorAlert variant="no-data" count={count} />
    }

    return (
        <Grid.Container>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <div className="ndd-rgd-component">
                    <GeneralizedComponent
                        payload={payload}
                        title={nddTitle}
                        scrollableWidth={true}
                        widths={[120, 100, 80, 80, 80]}
                        pinFirstColumn={true}
                        sortable={false}
                        kpiName="ndd rgd"
                        columnSize={12}
                    />
                </div>
            </Grid.Item>
        </Grid.Container>
    )
}
